import { Badge, Tooltip } from '@primer-io/goat';
import { useEffect, useMemo } from 'react';
import useSWR from 'swr';

import { useFetchWorkflows } from 'MODULES/workflows/api/useFetchWorkflows';
import {
  fetchMonitors,
  fetchMonitorsSummary,
  MONITORS,
  MONITORS_SUMMARY,
} from 'SRC/fetch/api/monitors';
import { MonitorsProductAccessedValue } from 'SRC/fetch/api/productEvents';
import { useFetchObsMe } from 'SRC/hooks/useFetchObs/useFetchObsMe';

type Props = {
  setMonitorsState: (state: MonitorsProductAccessedValue) => void;
  hasVisitedMonitors: boolean | undefined;
};

export const MonitorsAlert = ({
  setMonitorsState,
  hasVisitedMonitors,
}: Props) => {
  const { data } = useFetchObsMe();

  const userHasMonitorsPro = data?.monitorsPlan === 'PRO';

  const {
    data: { triggeredMonitorsLastHour },
    isLoading: isLoadingTriggeredMonitorsLastHour,
  } = useSWR(MONITORS_SUMMARY, fetchMonitorsSummary, {
    fallbackData: { triggeredMonitorsLastHour: 0 },
    refreshInterval: 30 * 1000,
  });

  const { data: monitorsData, isLoading: isLoadingMonitorsData } = useSWR(
    MONITORS,
    fetchMonitors,
  );

  const { data: workflowsData, isLoading: isLoadingWorkflowsData } =
    useFetchWorkflows({
      queryParams: {
        trigger_action_id: 'monitor_event_received',
        trigger_application_id: 'MONITORS',
        page_size: '1',
        status: 'PUBLISHED',
      },
    });

  useEffect(() => {
    if (
      isLoadingTriggeredMonitorsLastHour ||
      isLoadingMonitorsData ||
      isLoadingWorkflowsData
    ) {
      return;
    }

    if (triggeredMonitorsLastHour && userHasMonitorsPro) {
      setMonitorsState('alerting');
    } else if (
      userHasMonitorsPro &&
      monitorsData?.count &&
      monitorsData?.count > 0 &&
      workflowsData?.total_results_count === 0
    ) {
      setMonitorsState('notifications_warning');
    } else if (userHasMonitorsPro && triggeredMonitorsLastHour === 0) {
      setMonitorsState('not_alerting');
    } else if (!hasVisitedMonitors && !userHasMonitorsPro) {
      setMonitorsState('no_monitors_new_badge');
    }
  }, [
    isLoadingTriggeredMonitorsLastHour,
    isLoadingMonitorsData,
    isLoadingWorkflowsData,
    triggeredMonitorsLastHour,
    userHasMonitorsPro,
    monitorsData,
    workflowsData,
    hasVisitedMonitors,
    setMonitorsState,
  ]);

  const alertBadge = useMemo(() => {
    if (
      isLoadingTriggeredMonitorsLastHour ||
      isLoadingMonitorsData ||
      isLoadingWorkflowsData
    ) {
      return null;
    }

    if (triggeredMonitorsLastHour && userHasMonitorsPro) {
      return (
        <Badge sentiment='negative' contrast='low'>
          {triggeredMonitorsLastHour}
        </Badge>
      );
    }

    if (
      userHasMonitorsPro &&
      monitorsData?.count &&
      monitorsData?.count > 0 &&
      workflowsData?.total_results_count === 0
    ) {
      return (
        <Tooltip>
          <Tooltip.Trigger>
            <Badge sentiment='attentive' />
          </Tooltip.Trigger>
          <Tooltip.Content>
            No monitor notifications set up yet.
          </Tooltip.Content>
        </Tooltip>
      );
    }

    if (!hasVisitedMonitors && !userHasMonitorsPro) {
      return (
        <Badge sentiment='informative' contrast='low'>
          NEW
        </Badge>
      );
    }

    return null;
  }, [
    isLoadingTriggeredMonitorsLastHour,
    isLoadingMonitorsData,
    isLoadingWorkflowsData,
    triggeredMonitorsLastHour,
    userHasMonitorsPro,
    monitorsData,
    workflowsData,
    hasVisitedMonitors,
  ]);

  return <>{alertBadge}</>;
};
